/**
 * Botanical phone wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wallpapers_iv`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const botanicalPhoneWallpapersPack: ToilePack = {
    slug: 'botanical-phone-wallpapers',
    category: 'screens',
    name: 'Botanical phone wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'jungle', label: 'Leaf jungle', subject: 'overlapping monstera, banana and palm leaves in deep greens filling the screen, a little light between them'},
        {key: 'ferns', label: 'Fern plate', subject: 'fern fronds arranged as on a vintage botanical plate, fine detail on an aged cream paper ground'},
        {key: 'eucalyptus', label: 'Eucalyptus', subject: 'a single eucalyptus stem curving up from the bottom edge, round silvery leaves, on a plain sage ground'},
    ],
    styles: [
        {key: 'jungle', label: 'Lush jungle', style: 'lush layered foliage photograph look, deep shadow between leaves, dew and soft light', palette: 'deep green, emerald and a little gold', framing: 'Full-bleed, plants filling the screen with the top kept calm'},
        {key: 'plate', label: 'Engraved plate', style: 'antique botanical engraving, fine ink lines, delicate hand-coloring, aged paper', palette: 'sage green, ink brown and cream', framing: 'Full-bleed, the plant centered on paper with a clear margin of space above'},
        {key: 'matisse', label: 'Cut-out', style: 'Matisse-style paper cut-out, flat bold leaf shapes, a gallery print feel', palette: 'sage, olive and terracotta on a cream ground', framing: 'Full-bleed, shapes grouped in the lower two thirds'},
    ],
    directions: ['cactus', 'fern forest', 'white flowers'],
    directionHint: 'try: cactus',
    seo: {
        title: 'Botanical Phone Wallpapers: Leaves, Fern Plate, Eucalyptus',
        metaDescription: 'Botanical phone wallpapers in calm green: overlapping monstera and palm leaves, a fern plate and a eucalyptus stem, in three styles.',
        h1: 'Botanical wallpapers: leaf jungle, a fern plate, a eucalyptus stem',
        tagline: 'Nine green phone wallpapers, three plant scenes in three styles.',
        intro: 'Phone wallpapers of leaves and plants: monstera and palm leaves overlapping in deep greens, a fern plate in the manner of an old botanical book, and one eucalyptus stem on sage. Each comes as a lush jungle photo look, an antique engraving and a Matisse-style paper cut-out. Plant lovers can set them as they are. Type a different plant, such as a cactus, and the styles carry it.',
        useCases: [
            'a phone lock screen',
            'a gift for a plant lover',
            'a print for a plant corner or kitchen',
            'a calm tablet background',
        ],
        faq: [
            {q: 'What are the nine pictures?', a: 'Three plant scenes: overlapping jungle leaves, a fern plate, and a single eucalyptus stem. Each scene is shown in three styles.'},
            {q: 'Can I change the plant?', a: 'Yes. Type a plant such as a cactus, a fern forest or white flowers; a typed plant is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Lush jungle is layered foliage with dew and soft light. Engraved plate is fine ink lines on aged paper. Cut-out is flat, bold leaf shapes in sage and terracotta.'},
            {q: 'Are the ferns scanned from a real book?', a: 'No. The engraved style imitates an antique botanical plate, but all nine pictures are new rendered images.'},
        ],
    },
}
